/**
 * Scrollable Card List Component
 * Reusable component for horizontal scrollable card lists with navigation arrows
 * Follows Single Responsibility Principle
 */

import React, { useState, useEffect, useRef } from 'react';
import { spacing } from '@/design-system/utils';
import { LeftArrowButton, RightArrowButton } from './arrow-buttons';

export interface ScrollableCardListProps {
  children: React.ReactNode;
  showArrows?: boolean;
  scrollAmount?: number;
  onScroll?: () => void;
  className?: string;
}

export const ScrollableCardList: React.FC<ScrollableCardListProps> = ({
  children,
  showArrows = true,
  scrollAmount = 300,
  onScroll,
  className,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollability();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollability);
      window.addEventListener('resize', checkScrollability);
      return () => {
        container.removeEventListener('scroll', checkScrollability);
        window.removeEventListener('resize', checkScrollability);
      };
    }
  }, [children]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth',
      });
      onScroll?.();
    }
  };

  return (
    <div className={className} style={{ position: 'relative' }}>
      {showArrows && canScrollLeft && (
        <LeftArrowButton
          onClick={() => handleScroll('left')}
          ariaLabel='Scroll left'
        />
      )}
      <div
        ref={scrollContainerRef}
        style={{
          display: 'flex',
          gap: spacing[3],
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: spacing[2],
        }}
        className='scrollbar-hide'
        onScroll={checkScrollability}
      >
        {children}
      </div>
      {showArrows && canScrollRight && (
        <RightArrowButton
          onClick={() => handleScroll('right')}
          ariaLabel='Scroll right'
        />
      )}
    </div>
  );
};
